import React, { useEffect, useState } from 'react';
import { Modal } from "antd";
import { IImagePreviewModal } from "../type/media-hub.types";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Form } from "antd";
import { Download, Trash, Pencil, Info, EyeOff, Save } from "lucide-react";
import AddTags from "../../common/tags/add-tags";
import LoadingSkeleton from "../../common/loading-skeleton/loading-skeleton";
import { Document, MetaData } from "../../../domains/models/document";
import { buildFormElements } from "../../common/upload-document-modal/utils/buildMetaFormELements";
import { getMetaValuesFromUI } from "../../common/upload-document-modal/utils/document-utils";
import { updateMetaData } from "../action/media-hub.actions";
import dayjs from 'dayjs';
import { getImageUrl } from "../../../utility/utility";
import "../../../styles/component/image-preview-modal.scss";


const formatImg = (item: Document) => {

    return {
        id: item.id,
        name: item.originalFileName,
        previewUrl: getImageUrl(item, "large"),
        originalUrl: getImageUrl(item, "original"),
        metaData: item.metaData,
        fileName: item.fileName,
        size: item.size,
        uploadedDate: item.auditInfo?.createdOn ?? '',
        uploadedBy: item.auditInfo?.createdBy ?? ''
    }

}

const ImagePreviewModal: React.FC<IImagePreviewModal> = (props) => {
    const [showMetaInfo, setShowMetaInfo] = useState(props.showInfo ?? false);
    const [imgLoading, setImgLoading] = useState(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isDirty, setIsDirty] = useState(false);
    const basicDetailsMeta = props.UIMetaFields.filter((meta: MetaData) => meta.metaField !== 'tag');
    const [tags, setTags] = useState<string[]>([]);

    const [form] = Form.useForm();

    const values = props.imgDetails.metaData?.reduce((acc, item) => {
        acc[item.metaField] = item.metaValue;
        return acc;
    }, {} as Record<string, any>);

    useEffect(() => {
        setShowMetaInfo(props.showInfo ?? false);
    }, [props.showInfo]);

    useEffect(() => {
        form.resetFields();
        if (props.imgDetails.metaData) {
            form.setFieldsValue({
                ...values,
                capturedDate: values?.capturedDate
                    ? dayjs(values.capturedDate)
                    : null
            });

            const tagValues =
                props.imgDetails.metaData?.find(
                    item => item.metaField === "tag"
                )?.metaValue.split(',') || [];

            setTags(tagValues);
        }
        return () => {
            setIsEditing(false);
            setIsDirty(false);

        }

    }, [props.imgDetails.metaData]);


    async function saveChanges() {
        const formValues = form.getFieldsValue();

        const nonTagMeta = getMetaValuesFromUI(
            basicDetailsMeta,
            formValues
        );

        const tagMeta = {
            label: 'Tag',
            metaType: 'STRING',
            metaField: "tag",
            metaValue: tags.join(',')
        };

        const updatedMeta = [
            ...nonTagMeta,
            tagMeta
        ];

        await updateMetaData(
            props.imgDetails.id,
            updatedMeta
        );

        setIsDirty(false);
        setIsEditing(false);
    }

    const img = formatImg(props.imgDetails);
    return (
        <Modal
            open={props.isOpen}
            footer={null}
            onCancel={() => {
                props.handleModalClose();
                setShowMetaInfo(false);
            }}
            centered
            width={"auto"}
            className={`hrms-app media-image-preview-modal ${
                showMetaInfo ? "media-image-preview-modal--info-details" : ""
            }`}
            maskClosable={true}
            closable={true}
        >
            <div className="media-preview">
                <div
                    className="media-preview__nav media-preview__nav--prev"
                    onClick={props.goToPreviousImage}
                >
                    <LeftOutlined/>
                </div>
                <LoadingSkeleton loading={imgLoading}>
                    <div className="media-preview__content">

                        <div className="media-preview__image-container">
                            <img
                                onLoad={() => setImgLoading(false)}
                                src={!showMetaInfo ? img.originalUrl : img.previewUrl}
                                alt="preview"
                                className="media-preview__image"
                            />
                            <span className={`media-preview__info-icon-wrapper ${showMetaInfo ? 'show' : 'hide'}`}
                                  onClick={() => setShowMetaInfo(!showMetaInfo)}>
                               {!showMetaInfo ? <Info/> : <EyeOff/>}
                            </span>

                        </div>

                        <div className="media-preview__details">
                            <div className={'media-preview__details-header'}>
                                <h4> {img.name}</h4>
                            </div>
                            <div className="media-preview__metadata">
                                <div className="media-preview__details-tab">
                                    <div className="media-preview__section">
                                        <div className="media-preview__section-header">
                                            <h5 className={'title'}>Basic Details</h5>
                                            <span className={`action-btn ${isEditing ? 'active' : 'default'}`}
                                                  onClick={() => setIsEditing(!isEditing)}>
                                                <Pencil className={'edit-btn'}/>
                                            </span>
                                        </div>
                                        <Form
                                            form={form}
                                            layout={'horizontal'}
                                            className={'media-preview__meta-info-form'}
                                            disabled={!isEditing}
                                            onValuesChange={() => setIsDirty(true)}
                                        >
                                            {basicDetailsMeta?.length
                                                ? buildFormElements(basicDetailsMeta)
                                                : null}
                                        </Form>

                                    </div>

                                    <div className="media-preview__section">
                                        <div className="media-preview__section-header">
                                            <h5 className={'title'}>File Info</h5>
                                            <span className={'description'}>read only</span>
                                        </div>

                                        <div className="media-preview__field-list">
                                            <div className="media-preview__field">
                                                <label className={"media-preview__field-label"}>File Name</label>
                                                <span
                                                    className={"media-preview__field-value"}>{img.fileName}</span>
                                            </div>

                                            <div className="media-preview__field">
                                                <label className={"media-preview__field-label"}>Size</label>
                                                <span
                                                    className={"media-preview__field-value"}>{img.size}</span>
                                            </div>

                                            <div className="media-preview__field">
                                                <label className={"media-preview__field-label"}>Format</label>
                                                <span>{values?.format || 'N/A'}</span>
                                            </div>

                                            <div className="media-preview__field">
                                                <label className={"media-preview__field-label"}>Uploaded By</label>
                                                <span
                                                    className={"media-preview__field-value"}>{img.uploadedBy}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="media-preview__section">
                                        <div className="media-preview__section-header">
                                            <h5 className={'title'}>Camera Details</h5>
                                            <span className={'description'}>read only</span>
                                        </div>

                                        <div className="media-preview__field-list">
                                            <div className="media-preview__field">
                                                <label className={"media-preview__field-label"}>Device</label>
                                                <span className={"media-preview__field-value"}>N/A</span>
                                            </div>

                                            <div className="media-preview__field">
                                                <label className={"media-preview__field-label"}>Shutter</label>
                                                <span className={"media-preview__field-value"}>N/A</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="media-preview__tags-tab">
                                    <div className="media-preview__section-header">
                                        <h5 className={'title'}>Tags</h5>
                                    </div>
                                    <div className="media-preview__tags-container">
                                        <AddTags tagLabel="add tags" value={tags}
                                                 onChange={(val) => {
                                                     setTags(val);
                                                     setIsDirty(true);
                                                 }}/>
                                    </div>
                                </div>

                            </div>


                            <div className="media-preview__actions">
                                {isDirty && (
                                    <Button icon={<Save/>} onClick={saveChanges}>
                                        Save
                                    </Button>
                                )}
                                {props.isDeleteBtnVisible && <Button icon={<Trash/>} onClick={() => {
                                    props.onDelete(img.id);
                                    props.handleModalClose();
                                }}>Delete</Button>}
                                <Button icon={<Download/>} onClick={() => {
                                    props.onDownload(props.imgDetails);
                                }}>Download</Button>
                            </div>


                        </div>

                    </div>
                </LoadingSkeleton>
                <div
                    className="media-preview__nav media-preview__nav--next"
                    onClick={props.goToNextImage}
                >
                    <RightOutlined/>
                </div>
            </div>

        </Modal>
    )
};


export default ImagePreviewModal;
